import { ArrowRight, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Link, useParams } from "react-router";

import { Eyebrow } from "@/components/eyebrow";
import { MarkdownArticle } from "@/components/markdown-article";
import { PageShell } from "@/components/page-shell";
import { Scorecard } from "@/components/scorecard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { trackOnce } from "@/lib/analytics";
import { type SharedAssessmentRecord, useSharedAssessment } from "@/lib/api";

/**
 * Keeps a `<meta name="robots" content="noindex, nofollow">` in the document
 * head for as long as a shared report is mounted.
 *
 * Defense in depth only. nginx tags the index.html response with `X-Robots-Tag`
 * (see nginx.conf.template), which is what actually stops a crawler that never
 * runs our JavaScript. This meta tag covers JS-executing crawlers and the dev
 * server, where nginx is not in the request path.
 */
function useNoindexMeta() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}

/**
 * World-readable, view-only report resolved from an unguessable share token.
 * Anonymous: no route guard, no owner controls, and the payload carries no
 * owner details or expert-calibration internals.
 */
export function SharedReportPage() {
  const { token } = useParams<{ token: string }>();
  const query = useSharedAssessment(token ?? null);
  useNoindexMeta();

  const status = (query.error as { status?: number } | null)?.status;

  // Count a real open (data resolved), not a mount, so dead links don't inflate
  // the funnel. Keyed by the token for StrictMode/remount dedupe: the token is
  // the sole credential, so it is only ever the local key, never the payload.
  if (query.data && token) {
    trackOnce(token, "shared_link_opened", { method: "link" });
  }

  return (
    <PageShell>
      {query.isLoading && (
        <div className="flex justify-center py-24">
          <Loader2 className="text-muted h-6 w-6 animate-spin" />
        </div>
      )}
      {query.error != null &&
        (status === 404 ? (
          <div className="py-12 text-center">
            <Eyebrow>Unavailable</Eyebrow>
            {/* The API returns one identical 404 for an unknown, a revoked, and an
                ownerless token, so it can't be used to probe which reports exist.
                The copy must not claim a cause the API deliberately withholds. */}
            <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
              This link isn’t available.
            </h1>
            <p className="text-muted-foreground mt-4">
              It may have been turned off by whoever shared it, or the address may be mistyped. Ask
              them for a fresh link, or run an assessment for your own company.
            </p>
            <Button asChild className="mt-8">
              <Link to="/assess">Start an assessment</Link>
            </Button>
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Couldn’t load the report. Please try again.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ))}
      {query.data && <SharedReportBody record={query.data} />}
    </PageShell>
  );
}

interface SharedReportBodyProps {
  record: SharedAssessmentRecord;
}

function SharedReportBody({ record }: SharedReportBodyProps) {
  // Both fields are optional on the public projection, so build the meta line
  // from whatever is present rather than emitting a dangling separator.
  const context = [record.sector, record.geography].filter(Boolean).join(" · ");

  return (
    <div>
      <Eyebrow>AI Disruption Exposure Report</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        {record.company_name}.
      </h1>
      <p className="text-muted mt-2">
        {context ? `${context} · ` : ""}generated {new Date(record.created_at).toLocaleDateString()}
      </p>

      <Card className="mt-6">
        <CardContent className="pt-6">
          <Scorecard
            businessModel={record.business_model_disruption_score}
            operatingModel={record.operating_model_disruption_score}
            protector={record.structural_protector_score}
            overall={record.overall_exposure_score}
            confidence={record.confidence_level}
            exposureLevel={record.exposure_level}
            strategicPosition={record.strategic_position}
          />
        </CardContent>
      </Card>

      <Card className="mt-5">
        <CardContent className="pt-6">
          <MarkdownArticle markdown={record.report_markdown} />
        </CardContent>
      </Card>

      <Card className="border-accent mt-8">
        <CardContent className="pt-6 text-center">
          <Eyebrow>Your turn</Eyebrow>
          <h2 className="text-primary mt-3 text-3xl font-normal tracking-tight">
            Get your own <span className="text-accent">AI disruption assessment</span>.
          </h2>
          <p className="text-muted-foreground mx-auto mt-3 max-w-[52ch] text-sm">
            Answer a short set of questions about your company and get a report like this one.
          </p>
          <Button asChild className="mt-6">
            <Link to="/assess">
              Start your assessment
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
